import { useScrollAnimation } from "@/hooks/use-scroll-animation";

type Animation = "fade-up" | "fade-down" | "fade-left" | "fade-right" | "scale-up" | "blur-in";

interface AnimateOnScrollProps {
  animation?: Animation;
  delay?: number;
  duration?: number;
  threshold?: number;
  triggerOnce?: boolean;
  className?: string;
  children: React.ReactNode;
}

const initialStyles: Record<Animation, React.CSSProperties> = {
  "fade-up": { opacity: 0, transform: "translateY(24px)" },
  "fade-down": { opacity: 0, transform: "translateY(-24px)" },
  "fade-left": { opacity: 0, transform: "translateX(-32px)" },
  "fade-right": { opacity: 0, transform: "translateX(32px)" },
  "scale-up": { opacity: 0, transform: "scale(0.95)" },
  "blur-in": { opacity: 0, filter: "blur(8px)", transform: "translateY(8px)" },
};

const visibleStyles: Record<Animation, React.CSSProperties> = {
  "fade-up": { opacity: 1, transform: "translateY(0)" },
  "fade-down": { opacity: 1, transform: "translateY(0)" },
  "fade-left": { opacity: 1, transform: "translateX(0)" },
  "fade-right": { opacity: 1, transform: "translateX(0)" },
  "scale-up": { opacity: 1, transform: "scale(1)" },
  "blur-in": { opacity: 1, filter: "blur(0px)", transform: "translateY(0)" },
};

export function AnimateOnScroll({
  animation = "fade-up",
  delay = 0,
  duration = 600,
  threshold = 0.15,
  triggerOnce = true,
  className = "",
  children,
}: AnimateOnScrollProps) {
  const { ref, isVisible } = useScrollAnimation({ threshold, triggerOnce });

  const transitionProperty = animation === "blur-in"
    ? "opacity, transform, filter"
    : "opacity, transform";

  const style: React.CSSProperties = {
    ...(isVisible ? visibleStyles[animation] : initialStyles[animation]),
    transitionProperty,
    transitionDuration: `${duration}ms`,
    transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)",
    transitionDelay: `${delay}ms`,
    willChange: "opacity, transform",
  };

  return (
    <div ref={ref} style={style} className={className}>
      {children}
    </div>
  );
}
